import React, { useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Check, X, Loader2, CreditCard, Clock3, Moon, PoundSterling } from "lucide-react";
import { Button } from "./ui/button";
import { useFxRates, convert } from "../lib/currency";

const PLANS = [
  {
    id: "lite", name: "Lite", price: 45.99, tagline: "Get Ava answering, capturing every lead.",
    features: [
      { t: "250 AI credits / month", ok: true },
      { t: "Ava answering 24/7", ok: true },
      { t: "Lead capture & qualifying", ok: true },
      { t: "Shareable receptionist link", ok: true },
      { t: "Bookings & callbacks", ok: true },
      { t: "1 Ava tone", ok: true },
      { t: "AI follow-up messages", ok: false },
      { t: "Profit & earnings tracking + charts", ok: false },
      { t: "Website embed widget", ok: false },
      { t: "All Ava tones", ok: false },
    ],
  },
  {
    id: "pro", name: "Pro", price: 75.99, popular: true, tagline: "The full receptionist — win back every job.",
    features: [
      { t: "500 AI credits / month", ok: true },
      { t: "Ava answering 24/7", ok: true },
      { t: "Lead capture & qualifying", ok: true },
      { t: "Shareable receptionist link", ok: true },
      { t: "Bookings & callbacks", ok: true },
      { t: "All Ava tones", ok: true },
      { t: "AI follow-up messages", ok: true },
      { t: "Profit & earnings tracking + charts", ok: true },
      { t: "Website embed widget", ok: true },
    ],
  },
];

export default function SubscribeHardSell({ heading = "CHOOSE YOUR PLAN", subheading, currency = "GBP", currentPlan = null }) {
  const [loading, setLoading] = useState(null);
  const [billing, setBilling] = useState(() => {
    try { return localStorage.getItem("td_interval") === "year" ? "year" : "month"; } catch { return "month"; }
  });
  const chooseBilling = (v) => { setBilling(v); try { localStorage.setItem("td_interval", v); } catch {} };
  const rates = useFxRates();

  const subscribe = async (plan) => {
    setLoading(plan);
    try {
      const res = await api.post("/payments/checkout", { origin_url: window.location.origin, plan, interval: billing });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
      setLoading(null);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="subscribe-hardsell">
      <div className="text-center mb-6">
        <h1 className="font-display text-4xl md:text-5xl tracking-tight">{heading}</h1>
        <p className="text-muted-foreground mt-3 max-w-xl mx-auto">{subheading || "Pick the plan that fits — win back a single job and Ava's already paid for herself."}</p>
      </div>

      <div className="flex justify-center mb-7">
        <div className="inline-flex items-center bg-[#18181b] border border-white/10 rounded-full p-1" data-testid="billing-toggle">
          <button onClick={() => chooseBilling("month")} data-testid="billing-monthly" className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${billing === "month" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>Monthly</button>
          <button onClick={() => chooseBilling("year")} data-testid="billing-yearly" className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5 ${billing === "year" ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"}`}>Yearly <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded-full ${billing === "year" ? "bg-white/20 text-white" : "bg-primary/15 text-primary"}`}>1 month free</span></button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mb-8">
        {PLANS.map((plan) => {
          const yearly = +(plan.price * 11).toFixed(2);
          const shown = billing === "year" ? yearly : plan.price;
          const price = convert(shown, currency, rates);
          const isCurrent = currentPlan === plan.id;
          return (
            <div key={plan.id} className={`rounded-sm p-6 relative flex flex-col ${plan.popular ? "bg-[#18181b] border-2 border-primary" : "bg-[#18181b] border border-white/10"}`} data-testid={`plan-card-${plan.id}`}>
              {plan.popular && <div className="absolute top-4 right-4 bg-primary text-white text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-sm">Most popular</div>}
              <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">TradieDesk {plan.name}</p>
              <div className="flex items-end gap-2 mb-1">
                <span className={`font-display text-5xl tracking-tight ${plan.popular ? "text-primary" : "text-foreground"}`}>£{shown}</span>
                <span className="text-muted-foreground mb-1.5 text-sm">/ {billing === "year" ? "year" : "month"}</span>
              </div>
              {billing === "year" && <p className="text-xs text-primary mb-2" data-testid={`plan-annual-note-${plan.id}`}>£{plan.price}/mo billed annually · 1 month free</p>}
              {price.isConverted && <p className="text-xs text-muted-foreground mb-2" data-testid={`plan-converted-${plan.id}`}>≈ {price.text}/{billing === "year" ? "yr" : "mo"} · shown in your local currency at checkout</p>}
              {billing === "month" && <button onClick={() => chooseBilling("year")} data-testid={`plan-annual-nudge-${plan.id}`} className="text-xs text-primary hover:underline text-left mb-2 block">Pay yearly & get a month free — save £{plan.price}/yr →</button>}
              <p className="text-sm text-muted-foreground mb-5">{plan.tagline}</p>
              <ul className="space-y-2.5 mb-6 flex-1">
                {plan.features.map((f) => (
                  <li key={f.t} className={`flex items-start gap-2.5 text-sm ${f.ok ? "" : "text-muted-foreground/60"}`}>
                    <span className={`h-5 w-5 rounded-sm flex items-center justify-center shrink-0 mt-0.5 ${f.ok ? "bg-primary/15" : "bg-white/5"}`}>
                      {f.ok ? <Check className="h-3.5 w-3.5 text-primary" /> : <X className="h-3.5 w-3.5 text-muted-foreground/60" />}
                    </span>
                    {f.t}
                  </li>
                ))}
              </ul>
              <Button
                data-testid={`subscribe-${plan.id}-btn`}
                onClick={() => subscribe(plan.id)}
                disabled={!!loading || isCurrent}
                className={`w-full rounded-sm h-12 text-base font-semibold gap-2 ${plan.popular ? "bg-primary hover:bg-orange-600 text-white" : "bg-[#0a0a0a] hover:bg-black text-white border border-white/15"}`}
              >
                {loading === plan.id ? <Loader2 className="h-4 w-4 animate-spin" /> : isCurrent ? "Your current plan" : <><CreditCard className="h-4 w-4" /> {currentPlan === "lite" && plan.id === "pro" ? "Upgrade to Pro" : `Choose ${plan.name}`}</>}
              </Button>
            </div>
          );
        })}
      </div>

      {/* The maths */}
      <div className="grid md:grid-cols-2 gap-4 mb-6">
        <div className="bg-[#141416] border border-white/10 rounded-sm p-6">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">A human receptionist</p>
          <div className="flex items-end gap-2 mb-4"><span className="font-display text-4xl tracking-tight text-muted-foreground">£18,000</span><span className="text-muted-foreground mb-1 text-sm">/ yr+</span></div>
          <ul className="space-y-2.5">
            {["Clocks off at 5pm — misses evening & weekend jobs", "Sick days, holidays & lunch breaks", "One call at a time"].map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-sm text-muted-foreground"><span className="h-5 w-5 rounded-sm bg-white/5 flex items-center justify-center shrink-0 mt-0.5"><X className="h-3.5 w-3.5 text-red-400" /></span>{t}</li>
            ))}
          </ul>
        </div>
        <div className="bg-[#141416] border border-primary/40 rounded-sm p-6">
          <p className="text-xs uppercase tracking-wider text-primary mb-2">TradieDesk — Ava</p>
          <div className="flex items-end gap-2 mb-4"><span className="font-display text-4xl tracking-tight text-primary">from £45.99</span><span className="text-muted-foreground mb-1 text-sm">/ mo</span></div>
          <ul className="space-y-2.5">
            {["Works 24/7/365 — books jobs while you sleep", "Never sick, never late, never off", "Answers unlimited enquiries at once"].map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-sm"><span className="h-5 w-5 rounded-sm bg-primary/15 flex items-center justify-center shrink-0 mt-0.5"><Check className="h-3.5 w-3.5 text-primary" /></span>{t}</li>
            ))}
          </ul>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {[
          { icon: Clock3, big: "24/7", small: "Always answering, never off the clock" },
          { icon: Moon, big: "3am", small: "Books the emergency call-out you'd have missed" },
          { icon: PoundSterling, big: "£1.53", small: "A day on Lite — less than half a coffee" },
        ].map((s) => (
          <div key={s.big} className="bg-[#18181b] border border-white/10 rounded-sm p-4 flex items-center gap-3">
            <div className="h-10 w-10 bg-primary/15 rounded-sm flex items-center justify-center shrink-0"><s.icon className="h-5 w-5 text-primary" /></div>
            <div><span className="font-display text-xl tracking-tight">{s.big}</span><p className="text-xs text-muted-foreground leading-tight">{s.small}</p></div>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground text-center mt-5">Secure payment via Stripe · Cancel anytime · No contract{currency !== "GBP" ? " · non-GBP cards may incur a small bank conversion fee" : ""}</p>
    </div>
  );
}
